import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { LINHAS_COM_META, nomeDoMes, somarMeses, type ChaveDeMeta, type Dre } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais, textoParaCentavos } from "@/lib/dinheiro";
import { campo, rotulo } from "../estilos";
import { metasDoRealizado, type Metas } from "./metas-do-dre";

type Props = { aberto: boolean; onFechar: () => void; competencia: string; metas: Metas; anoPassado: Dre | null };

const paraTexto = (m: Metas) => Object.fromEntries(LINHAS_COM_META.map((l) => [l.chave, m[l.chave] != null ? reais(m[l.chave]!) : ""])) as Record<ChaveDeMeta, string>;

/** Metas do mês por linha do DRE: digitadas ou copiadas do mesmo mês do ano passado com um ajuste. */
export function MetasDialog({ aberto, onFechar, competencia, metas, anoPassado }: Props) {
  const utils = trpc.useUtils();
  const [valores, setValores] = useState(paraTexto(metas));
  const [ajuste, setAjuste] = useState("10");
  const [resto, setResto] = useState(false);
  useEffect(() => {
    if (aberto) setValores(paraTexto(metas));
  }, [aberto, metas]);
  const salvar = trpc.financeiro.metas.salvar.useMutation({
    onSuccess: () => {
      toast.success("Metas salvas.");
      utils.financeiro.dre.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message),
  });
  const ultimo = `${competencia.slice(0, 4)}-12`;
  const meses: string[] = [];
  for (let m = competencia; m <= (resto ? ultimo : competencia); m = somarMeses(m, 1)) meses.push(m);
  const enviar = () =>
    salvar.mutate({
      competencias: meses,
      valores: Object.fromEntries(LINHAS_COM_META.map((l) => [l.chave, valores[l.chave].trim() ? textoParaCentavos(valores[l.chave]) || 0 : null])) as Record<ChaveDeMeta, number | null>,
    });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="flex max-h-[90dvh] max-w-lg flex-col gap-4 overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle>Metas de {nomeDoMes(competencia)}</DialogTitle>
          <DialogDescription>Quanto se quer faturar e gastar em cada linha. Os subtotais e o resultado da meta saem destas linhas. Linha em branco fica sem meta.</DialogDescription>
        </DialogHeader>
        {anoPassado && (
          <div className="flex flex-wrap items-end gap-2 rounded-lg bg-zinc-50 p-3 dark:bg-muted/40">
            <label className="w-24">
              <span className={rotulo}>Ajuste (%)</span>
              <input value={ajuste} onChange={(e) => setAjuste(e.target.value)} inputMode="decimal" className={campo} />
            </label>
            <button
              type="button"
              onClick={() => setValores(paraTexto(metasDoRealizado(anoPassado, Number(ajuste.replace(",", ".")) || 0)))}
              className="h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
            >
              Copiar de {nomeDoMes(somarMeses(competencia, -12))}
            </button>
          </div>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          {LINHAS_COM_META.map((l) => (
            <label key={l.chave} className="block">
              <span className={rotulo}>{l.rotulo}</span>
              <input value={valores[l.chave]} inputMode="decimal" placeholder="Sem meta" onChange={(e) => setValores({ ...valores, [l.chave]: e.target.value })} className={campo} />
            </label>
          ))}
        </div>
        {competencia < ultimo && (
          <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-foreground">
            <input type="checkbox" checked={resto} onChange={(e) => setResto(e.target.checked)} className="h-4 w-4 accent-brand-600" />
            Usar as mesmas metas até dezembro
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
            Cancelar
          </button>
          <button type="button" disabled={salvar.isPending} onClick={enviar} className="h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50">
            Salvar metas
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
